import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Send, Loader2, Beef, Fish, Sandwich, Shell, Wine, GlassWater, Grape, Sparkles, Cherry, Drumstick, Droplets, Landmark, Mountain, Sun, Leaf, TreePine, MapPin, RefreshCw, DollarSign, Gem, Crown } from 'lucide-react';
import ChatMessage from '../components/ChatMessage';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// New sommelier avatar
const SOMMELIER_AVATAR = "https://customer-assets.emergentagent.com/job_wine-sommelier-2/artifacts/vu5iglcz_att.ynPmhaFhSeLs5blyB0W6yY3b31D9r_dMDQ9FnJVxofo.jpeg";

// Restaurant-specific configurations
const RESTAURANT_CONFIG = {
  treadwell: {
    name: 'Treadwell Cuisine',
    displayName: 'THE SOMM',
    wineListUrl: "/Treadwell-Wine-List.pdf",
    logoUrl: "/treadwell-logo.png",
    splitPrompts: {
      left: {
        label: "Wine Styles",
        items: [
          { prompt: "I'd like a full-bodied bold red wine", icon: "wine", title: "Bold Red", iconBg: "bg-red-50", iconColor: "text-red-500" },
          { prompt: "Something crisp and dry white", icon: "glasswater", title: "Crisp & Dry White", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
          { prompt: "I'm in the mood for a rosé", icon: "grape", title: "Rosé", iconBg: "bg-pink-50", iconColor: "text-pink-400" },
          { prompt: "A light and fruity red wine", icon: "cherry", title: "Light & Fruity Red", iconBg: "bg-rose-50", iconColor: "text-rose-400" },
          { prompt: "An off-dry white wine please", icon: "droplets", title: "Off-Dry White", iconBg: "bg-teal-50", iconColor: "text-teal-500" }
        ]
      },
      right: {
        label: "Wine Regions",
        items: [
          { prompt: "A great Niagara red wine", icon: "mappin", title: "Niagara (Red)", iconBg: "bg-red-50", iconColor: "text-red-500" },
          { prompt: "A Niagara white wine please", icon: "mappin", title: "Niagara (White)", iconBg: "bg-sky-50", iconColor: "text-sky-500" },
          { prompt: "Champagne to celebrate", icon: "sparkles", title: "Champagne", iconBg: "bg-yellow-50", iconColor: "text-yellow-500" },
          { prompt: "I love a good Bordeaux", icon: "landmark", title: "Bordeaux", iconBg: "bg-blue-50", iconColor: "text-blue-500" },
          { prompt: "Red Burgundy, like a Pinot Noir", icon: "leaf", title: "Burgundy (Red)", iconBg: "bg-red-50", iconColor: "text-red-400" },
          { prompt: "White Burgundy, like a Chardonnay", icon: "sun", title: "Burgundy (White)", iconBg: "bg-green-50", iconColor: "text-green-500" },
          { prompt: "Italian red from Tuscany or Piedmont", icon: "mountain", title: "Piedmont & Tuscany", iconBg: "bg-orange-50", iconColor: "text-orange-500" },
          { prompt: "A great Napa Valley wine", icon: "treepine", title: "Napa Valley", iconBg: "bg-emerald-50", iconColor: "text-emerald-500" }
        ]
      }
    }
  },
  notl: {
    name: 'NOTL',
    displayName: 'THE SOMM',
    splitPrompts: {
      left: {
        label: "Wine Styles",
        items: [
          { prompt: "I'd like a bold red wine", icon: "wine", title: "Bold Red", iconBg: "bg-red-50", iconColor: "text-red-500" },
          { prompt: "Something crisp and white", icon: "glasswater", title: "Crisp White", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
          { prompt: "I'm in the mood for a rosé", icon: "grape", title: "Rosé", iconBg: "bg-pink-50", iconColor: "text-pink-400" },
          { prompt: "Something sparkling please", icon: "sparkles", title: "Sparkling", iconBg: "bg-violet-50", iconColor: "text-violet-400" },
          { prompt: "A light and fruity red", icon: "cherry", title: "Light & Fruity", iconBg: "bg-rose-50", iconColor: "text-rose-400" }
        ]
      },
      right: {
        label: "Pair with Food",
        items: [
          { prompt: "Wine for Steak Frites", icon: "beef", title: "Steak Frites", iconBg: "bg-red-50", iconColor: "text-red-500" },
          { prompt: "What pairs with the Salmon?", icon: "fish", title: "Salmon", iconBg: "bg-rose-50", iconColor: "text-rose-400" },
          { prompt: "Something for the NOTL Burger", icon: "sandwich", title: "NOTL Burger", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
          { prompt: "White wine for the Lobster Dip", icon: "shell", title: "Lobster Dip", iconBg: "bg-sky-50", iconColor: "text-sky-500" },
          { prompt: "Wine for Chicken Schnitzel", icon: "drumstick", title: "Schnitzel", iconBg: "bg-yellow-50", iconColor: "text-yellow-500" }
        ]
      }
    }
  }
};

const Chat = ({ restaurant = 'treadwell' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const config = RESTAURANT_CONFIG[restaurant] || RESTAURANT_CONFIG.treadwell;

  // Icon mapping for NOTL icon category cards
  const ICON_MAP = {
    beef: Beef,
    fish: Fish,
    sandwich: Sandwich,
    shell: Shell,
    wine: Wine,
    glasswater: GlassWater,
    grape: Grape,
    sparkles: Sparkles,
    cherry: Cherry,
    drumstick: Drumstick,
    droplets: Droplets,
    landmark: Landmark,
    mountain: Mountain,
    sun: Sun,
    leaf: Leaf,
    treepine: TreePine,
    mappin: MapPin,
    dollarsign: DollarSign,
    gem: Gem,
    crown: Crown,
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize session on mount or restaurant change
  useEffect(() => {
    const startNewSession = async () => {
      try {
        // Clear any stored session - always start fresh on page load
        const sessionKey = `sommelier_session_${restaurant}`;
        localStorage.removeItem(sessionKey);
        
        const response = await axios.post(`${API}/chat/message`, {
          message: 'start',
          session_id: null,
          restaurant: restaurant
        });
        
        const newSessionId = response.data.session_id;
        setSessionId(newSessionId);
        localStorage.setItem(sessionKey, newSessionId);
        
        // Add welcome message
        setMessages([{
          id: 0,
          content: response.data.response.content,
          isUser: false
        }]);
      } catch (error) {
        console.error('Error starting session:', error);
      }
    };

    // Reset messages when restaurant changes
    setMessages([]);
    setSessionId(null);
    startNewSession();
  }, [restaurant]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading || !sessionId) return;

    const userMessage = messageText.trim();
    setInput('');
    
    // Track the first real user message as the original prompt/style
    if (!originalPrompt) {
      setOriginalPrompt(userMessage);
    }
    
    // Reset refresh count when user sends a new message (new style selection)
    setRefreshCount(0);
    
    // Add user message
    setMessages(prev => [...prev, { id: Date.now(), content: userMessage, isUser: true }]);
    setIsLoading(true);

    try {
      // Call real API with restaurant parameter
      const response = await axios.post(`${API}/chat/message`, {
        message: userMessage,
        session_id: sessionId,
        restaurant: restaurant
      });

      // Add assistant response
      const assistantMessage = response.data.response;
      setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, content: assistantMessage, isUser: false }
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      setMessages(prev => [
        ...prev,
        { 
          id: Date.now() + 1, 
          content: 'Sorry, I encountered an error. Please try again.', 
          isUser: false 
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    await sendMessage(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-send when clicking a quick prompt
  const handleQuickPrompt = async (prompt) => {
    await sendMessage(prompt);
  };

  const [refreshCount, setRefreshCount] = useState(0);
  const [originalPrompt, setOriginalPrompt] = useState('');
  const MAX_REFRESHES = 3;

  // Handle refresh - silently get 3 more wines without showing a user message
  const handleRefresh = async () => {
    if (isLoading || !sessionId || refreshCount >= MAX_REFRESHES) return;
    setIsLoading(true);
    setRefreshCount(prev => prev + 1);

    // Find the most recent user message to know the current style
    const lastUserMsg = [...messages].reverse().find(m => m.isUser);
    const styleContext = lastUserMsg?.content || originalPrompt || '';

    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: `[REFRESH - same category] The guest originally asked: "${styleContext}". Recommend 3 DIFFERENT wines that match EXACTLY that same style/category. Do NOT ask questions. Do NOT change the style. Just show 3 new wines.`,
        session_id: sessionId,
        restaurant: restaurant
      });

      const assistantMessage = response.data.response;
      setMessages(prev => [
        ...prev,
        { id: Date.now(), content: assistantMessage, isUser: false }
      ]);
    } catch (error) {
      console.error('Error refreshing wines:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Find the last wine recommendation message index
  const lastWineRecIndex = messages.reduce((lastIdx, msg, idx) => {
    if (!msg.isUser && msg.content && typeof msg.content === 'object' && msg.content.type !== 'text') {
      return idx;
    }
    return lastIdx;
  }, -1);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex justify-center">
          <div className="flex flex-col items-center leading-none">
            <span className="text-[10px] font-medium tracking-[0.3em] text-gray-400">THE</span>
            <span className="text-lg font-semibold tracking-wide" style={{fontFamily: 'Georgia, serif', color: '#722F37'}}>SOMM</span>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message.content}
              isUser={message.isUser}
              onRefresh={undefined}
            />
          ))}
          
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="flex items-start gap-3">
                <img 
                  src={SOMMELIER_AVATAR}
                  alt="Sommelier"
                  className="h-9 w-9 rounded-full object-cover shrink-0"
                />
                <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Finding the perfect wine...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Wine List Link - Treadwell Only */}
      {messages.length === 1 && !isLoading && config.wineListUrl && (
        <div className="max-w-3xl mx-auto px-4 pb-4">
          <a 
            href={`https://docs.google.com/viewer?url=${window.location.origin}${config.wineListUrl}&embedded=false`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-gray-200 hover:border-[#D4704A] transition-all duration-200"
          >
            <img src={config.logoUrl} alt="Wine List" className="h-7 w-auto" />
            <span className="text-base font-medium text-gray-700">View Full Wine List</span>
          </a>
        </div>
      )}

      {/* Quick Prompts - Horizontal Scroll Sections */}
      {messages.length === 1 && !isLoading && config.splitPrompts && (() => {
        const columns = [config.splitPrompts.left, config.splitPrompts.right];
        if (config.splitPrompts.third) columns.push(config.splitPrompts.third);

        // Touch tracking for distinguishing taps from scrolls
        let touchStartX = 0;
        let touchStartY = 0;
        const handleTouchStart = (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        };
        const handleTouchEnd = (e, prompt) => {
          const dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
          const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
          // Only fire tap if finger barely moved (< 10px)
          if (dx < 10 && dy < 10) {
            handleQuickPrompt(prompt);
          }
        };

        return (
        <div className="w-full pb-3 space-y-3">
          {columns.map((col, colIdx) => {
            const testPrefix = ['left', 'right', 'third'][colIdx];
            return (
              <div key={colIdx} className="w-full">
                <p className="text-[10px] font-semibold tracking-[0.15em] text-[#D4704A] uppercase mb-1.5 px-4" data-testid={`split-${testPrefix}-label`}>{col.label}</p>
                <div
                  className="flex gap-2 overflow-x-scroll px-4 pb-2 w-full"
                  style={{scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch'}}
                >
                  {col.items.map((item, index) => {
                    const IconComponent = ICON_MAP[item.icon];
                    return (
                      <div
                        key={index}
                        role="button"
                        tabIndex={0}
                        data-testid={`${testPrefix}-card-${index}`}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={(e) => handleTouchEnd(e, item.prompt)}
                        onClick={() => handleQuickPrompt(item.prompt)}
                        className="flex-shrink-0 bg-white rounded-full border border-gray-100 py-2 px-3 flex items-center gap-2 snap-start select-none cursor-pointer"
                      >
                        <div className={`w-7 h-7 rounded-full ${item.iconBg} flex items-center justify-center shrink-0`}>
                          {IconComponent && <IconComponent className={`w-3.5 h-3.5 ${item.iconColor}`} strokeWidth={1.8} />}
                        </div>
                        <span className="text-[12px] font-medium text-gray-700 whitespace-nowrap">{item.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* Input */}
      <div className="bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-end gap-2 bg-gray-50 rounded-2xl p-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tell me what you like or what's for dinner tonight..."
              className="resize-none min-h-[44px] max-h-[120px] border-0 bg-transparent focus:ring-0 focus-visible:ring-0 text-base placeholder:text-gray-400"
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="sm"
              className="rounded-xl h-11 w-11 p-0 shrink-0 transition-all duration-200 bg-[#D4704A] hover:bg-[#B85A3A] disabled:bg-[#D4704A] disabled:opacity-100 shadow-md"
            >
              <Send className="h-5 w-5 text-white" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
